import { FeatureFlags } from "flagged";

import { getObject, RepositoryKey } from "utils/repository";

export const FLAG_AUDIT_LOGS = "audit_logs";
export const FLAG_ASSET_MANAGEMENT = "asset_management";
export const FLAG_FILE_SHARING = "file_sharing";
export const FLAG_MANUAL_ENTITLEMENT_LIMITATION = "manual_entitlement_limitation";
export const FLAG_SCHEDULED_LICENSE_DELIVERY = "scheduled_license_delivery";
export const FLAG_WORKFLOW_EDITOR_V3 = "workflow_editor_v3";
export const FLAG_AI_ADDONS = "ai-addons";
export const FLAG_AI_HOME = "ai_home_page";
export const FLAG_BAM_PORTAL = "bam_portal";
export const FLAG_UICOMPS_TENANTS_TABLE = "uicomps_tenants_table";
export const FLAG_UICOMPS_REPORT_PATHS_TABLE = "uicomps_report_paths_table";
export const FLAG_REPORT_SIGNATURE_KEYS = "report_signature_keys";
export const FLAG_BAM_GRADING = "bam_grading";
export const FLAG_BAM_REPORTS = "bam_reports";
export const FLAG_BAM_INTEGRATIONS = "bam_integrations";
export const FLAG_BAM_ADDONS = "bam_addons";
export const FLAG_MOBILE_NAVIGATION = "mobile_navigation";
export const FLAG_RESTRUCTURED_NAVIGATION = "restructured_navigation";
export const FLAG_AI_TENANT_STATE = "ai_tenant_state";
export const FLAG_DEVICE_PASSPORT = "device_passport";
export const FLAG_AI_LICENSE_CONSUMPTION = "ai_license_consumption";
export const FLAG_RESTRUCTURED_SETTINGS = "restructured_settings";
export const FLAG_REPORT_PUBLIC_LINKS = "report_public_links";
export const FLAG_RESTRUCTURED_ROLE_MANAGEMENT = "restructured_role_management";

// In production there won't be any overriding values in LocalStorage so this
// constant alone defines the flags there.
export const DEFAULT_FLAGS: FeatureFlags = {
    [FLAG_MANUAL_ENTITLEMENT_LIMITATION]: false,
    [FLAG_SCHEDULED_LICENSE_DELIVERY]: true,
    [FLAG_WORKFLOW_EDITOR_V3]: true,
    [FLAG_ASSET_MANAGEMENT]: true,
    [FLAG_FILE_SHARING]: true,
    [FLAG_UICOMPS_TENANTS_TABLE]: false,
    [FLAG_UICOMPS_REPORT_PATHS_TABLE]: true,
    [FLAG_AI_ADDONS]: false,
    [FLAG_AI_HOME]: true,
    [FLAG_AUDIT_LOGS]: true,
    [FLAG_BAM_PORTAL]: false,
    [FLAG_REPORT_SIGNATURE_KEYS]: true,
    [FLAG_BAM_GRADING]: true,
    [FLAG_BAM_REPORTS]: false,
    [FLAG_BAM_INTEGRATIONS]: true,
    [FLAG_BAM_ADDONS]: true,
    [FLAG_MOBILE_NAVIGATION]: false,
    [FLAG_RESTRUCTURED_NAVIGATION]: true,
    [FLAG_AI_TENANT_STATE]: true,
    [FLAG_DEVICE_PASSPORT]: true,
    [FLAG_AI_LICENSE_CONSUMPTION]: false,
    [FLAG_RESTRUCTURED_SETTINGS]: true,
    [FLAG_REPORT_PUBLIC_LINKS]: false,
    [FLAG_RESTRUCTURED_ROLE_MANAGEMENT]: true,
};

export type FeatureFlagKey = keyof typeof DEFAULT_FLAGS;

class FeatureFlagService {
    private flags: FeatureFlags;

    constructor() {
        this.flags = Object.assign({}, DEFAULT_FLAGS);
    }

    getFlags(): FeatureFlags {
        return this.flags;
    }

    // to use in non React components
    isFeatureEnabled(flag: FeatureFlagKey): boolean {
        const flags = this.flags;
        if (Array.isArray(flags)) {
            return false;
        }
        const value = flags[flag];
        return typeof value === "boolean" && value;
    }

    override(overriding: FeatureFlags): FeatureFlagService {
        this.flags = Object.assign({}, DEFAULT_FLAGS, overriding);
        return this;
    }
}

function readLocalStorageFlags(): FeatureFlags {
    return getObject<FeatureFlags>(RepositoryKey.FEATURE_FLAGS) ?? {};
}

export const featureFlagService = new FeatureFlagService().override(readLocalStorageFlags());
